import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { generateNewPageable, isPageTooHigh } from 'src/adminPanel/utils/helperClasses';
import { CommissionYearStatistics } from 'src/adminPanelV2/api/commissionsV2';
import { apiErrorMessage, CallApiResult } from 'src/api';
import {
    fetchCommissionEntriesByFilter,
    fetchCommissionStats,
    fetchMyCommissionStatistics,
} from 'src/api/commission-account';
import i18next from 'src/config/i18next';
import { commissionStatsFromYearStatistics } from 'src/entities/commission-account/CommissionStats';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import {
    fetchCommissionEntries,
    FETCH_COMMISSION_ENTRIES,
    FETCH_COMMISSION_STATS,
    FETCH_MY_COMMISSION_STATS,
    requestCommissionEntries,
    requestCommissionEntriesFailure,
    requestCommissionEntriesSuccess,
    requestCommissionStats,
    requestCommissionStatsFailure,
    requestCommissionStatsSuccess,
} from './actions';

/*  Commission Account Stats  */
function* fetchCommissionStatsSaga(action: AnyAction): SagaIterator {
    yield put(requestCommissionStats());

    const { error, response } = yield call(
        fetchCommissionStats,
        action.selectedYear,
        action.compareYear,
        action.commissionType,
        action.ignoreEligibility
    );

    if (error) yield put(requestCommissionStatsFailure(error));
    else yield put(requestCommissionStatsSuccess(response));
}

export function* generateFetchCommissionStatsWatcher() {
    yield takeLatest(FETCH_COMMISSION_STATS, fetchCommissionStatsSaga);
}

/* R65 item 8c: the selected and the compare year from ordering's live read */
function* fetchMyCommissionStatsSaga(action: AnyAction): SagaIterator {
    yield put(requestCommissionStats());

    const [selected, compare]: CallApiResult<CommissionYearStatistics>[] = yield all([
        call(fetchMyCommissionStatistics, action.selectedYear),
        call(fetchMyCommissionStatistics, action.compareYear),
    ]);

    if (selected.response && compare.response) {
        yield put(
            requestCommissionStatsSuccess({
                data: commissionStatsFromYearStatistics(
                    selected.response.data,
                    compare.response.data,
                    i18next.language
                ),
            })
        );
        return;
    }
    const error = selected.error ?? compare.error;
    yield put(requestCommissionStatsFailure(error));
    yield put(
        enqueueSnackbarAction({
            message: apiErrorMessage({ error }, i18next.t('translations:r63_comm_account.stats.load_error')),
            options: { key: new Date().getTime() + Math.random(), variant: 'error' },
        })
    );
}

export function* generateFetchMyCommissionStatsWatcher() {
    yield takeLatest(FETCH_MY_COMMISSION_STATS, fetchMyCommissionStatsSaga);
}

/* Commission Account Entries */
function* fetchCommissionEntriesSaga(action: AnyAction): SagaIterator {
    yield put(requestCommissionEntries());

    const { error, response } = yield call(
        fetchCommissionEntriesByFilter,
        action.year,
        action.month,
        action.commissionType,
        action.ignoreEligibility,
        action.pageable
    );

    if (error) yield put(requestCommissionEntriesFailure(error));
    else {
        if (response.data !== 'notPayoutEligible' && isPageTooHigh(response.data)) {
            yield put(
                fetchCommissionEntries(
                    action.year,
                    action.month,
                    action.commissionType,
                    action.ignoreEligibility,
                    generateNewPageable(action.pageable)
                )
            );
        } else {
            yield put(requestCommissionEntriesSuccess(response));
        }
    }
}

export function* generateFetchCommissionEntriesWatcher() {
    yield takeLatest(FETCH_COMMISSION_ENTRIES, fetchCommissionEntriesSaga);
}
